import { supabase } from './supabase';
import { loginWithFacebook } from './facebook';

interface TemplateMessage {
  name: string;
  language: {
    code: string;
  };
}

export async function sendWhatsAppMessage(instanceId: string, to: string, template: TemplateMessage) {
  try {
    // First verify the instance exists and get all necessary fields
    const { data: instance, error: instanceError } = await supabase
      .from('instances')
      .select('*')
      .eq('id', instanceId)
      .eq('is_active', true)
      .single();

    if (instanceError) {
      console.error('Database error:', instanceError);
      throw new Error('Error al acceder a la base de datos. Por favor, intente nuevamente.');
    }

    if (!instance) {
      console.error('No instance found with ID:', instanceId);
      throw new Error('Canal no encontrado o inactivo. Por favor, verifique el estado del canal.');
    }

    if (!instance.access_token || !instance.phone_number_id) {
      console.error('Instance missing required fields:', {
        id: instance.id,
        hasAccessToken: !!instance.access_token,
        hasPhoneNumberId: !!instance.phone_number_id
      });
      throw new Error('Configuración del canal incompleta. Por favor, verifique los datos del canal.');
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp/send`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
      },
      body: JSON.stringify({
        instanceId,
        to,
        template
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('WhatsApp API error:', errorData);

      // Handle token expiration
      if (errorData.error?.code === 190 && errorData.error?.error_subcode === 463) {
        throw new Error('TOKEN_EXPIRED');
      }

      // Handle specific WhatsApp API errors
      if (errorData.error?.code === 10) {
        throw new Error(
          'El token de acceso no tiene los permisos necesarios. ' +
          'Por favor, verifique que su aplicación de WhatsApp Business tenga los permisos correctos ' +
          'y que el token de acceso sea válido.'
        );
      }

      if (errorData.error?.code === 131030) {
        throw new Error(
          'El número de teléfono del destinatario no está en la lista de autorizados. ' +
          'Para enviar mensajes de prueba, primero debe agregar este número a la lista de números autorizados ' +
          'en su cuenta de WhatsApp Business.'
        );
      }

      if (errorData.error?.code === 131014) {
        throw new Error(
          'El número de teléfono no es válido o no tiene WhatsApp. ' +
          'Verifique que el número esté en el formato correcto e incluya el código de país.'
        );
      }

      throw new Error(
        errorData.error?.message || 
        'Error al enviar el mensaje. Por favor, verifique su configuración de WhatsApp Business.'
      );
    }

    const responseData = await response.json();
    return responseData;
  } catch (error: any) {
    console.error('Error in sendWhatsAppMessage:', error);
    throw error;
  }
}

export async function getInstanceMessages(instanceId: string) {
  try {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('instance_id', instanceId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
}